import { DecodeLogDataMismatch, DecodeLogTopicsMismatch, SocketClosedError, TimeoutError, UrlRequiredError, WebSocketRequestError, createPublicClient, decodeEventLog, encodeEventTopics, formatLog, hexToNumber, webSocket, withTimeout } from "viem";

//#region src/viem/actions/shred/watchContractShredEvent.ts
/**
* Watches and returns emitted contract events that have been processed and confirmed as shreds
* on the RISE network.
*
* @param client - Client to use.
* @param parameters - {@link WatchContractShredEventParameters}
* @returns A function that can be used to unsubscribe from the event. {@link WatchContractShredEventReturnType}
*/
function watchContractShredEvent(client, parameters) {
	const { abi, address, args, eventName, onError, onLogs, strict: strict_ } = parameters;
	const transport_ = (() => {
		if (client.transport.type === "webSocket") return client.transport;
		const wsTransport = client.transport?.transports.find((transport) => transport.config.type === "webSocket");
		if (!wsTransport) throw new Error("A shredWebSocket transport is required");
		return wsTransport.value;
	})();
	const subscribeShredContractEvent = () => {
		let active = true;
		let unsubscribe = () => {
			active = false;
		};
		(async () => {
			try {
				const topics = eventName ? encodeEventTopics({
					abi,
					eventName,
					args
				}) : [];
				const { unsubscribe: unsubscribe_ } = await transport_.riseSubscribe({
					params: ["logs", {
						address,
						topics
					}],
					onData(data) {
						if (!active) return;
						const log = data.result;
						try {
							const { eventName: eventName$1, args: args$1 } = decodeEventLog({
								abi,
								data: log.data,
								topics: log.topics,
								strict: strict_
							});
							const formatted = formatLog(log, {
								args: args$1,
								eventName: eventName$1
							});
							onLogs([formatted]);
						} catch (error) {
							let eventName$1;
							let isUnnamed;
							if (error instanceof DecodeLogDataMismatch || error instanceof DecodeLogTopicsMismatch) {
								if (strict_) return;
								eventName$1 = error.abiItem.name;
								isUnnamed = error.abiItem.inputs?.some((x) => !("name" in x) || !x.name);
							}
							const formatted = formatLog(log, {
								args: isUnnamed ? [] : {},
								eventName: eventName$1
							});
							onLogs([formatted]);
						}
					},
					onError(error) {
						onError?.(error);
					}
				});
				unsubscribe = unsubscribe_;
				if (!active) unsubscribe();
			} catch (error) {
				onError?.(error);
			}
		})();
		return () => unsubscribe();
	};
	return subscribeShredContractEvent();
}

//#endregion
//#region src/viem/actions/shred/watchShredEvent.ts
/**
* Watches and returns emitted events that have been processed and confirmed as shreds
* on the RISE network.
*
* @param client - Client to use.
* @param parameters - {@link WatchShredEventParameters}
* @returns A function that can be used to unsubscribe from the event. {@link WatchShredEventReturnType}
*/
function watchShredEvent(client, { address, args, event, events, onError, onLogs, strict: strict_ }) {
	const transport_ = (() => {
		if (client.transport.type === "webSocket") return client.transport;
		const wsTransport = client.transport?.transports.find((transport) => transport.config.type === "webSocket");
		if (!wsTransport) throw new Error("A shredWebSocket transport is required");
		return wsTransport.value;
	})();
	const isStrict = strict_ ?? false;
	const subscribeShredEvents = () => {
		let active = true;
		let unsubscribe = () => {
			active = false;
		};
		(async () => {
			try {
				const events_ = events ?? (event ? [event] : void 0);
				let topics = [];
				if (events_) {
					const encoded = events_.flatMap((event$1) => encodeEventTopics({
						abi: [event$1],
						eventName: event$1.name,
						args
					}));
					topics = [encoded];
					if (event) topics = topics[0];
				}
				const { unsubscribe: unsubscribe_ } = await transport_.riseSubscribe({
					params: ["logs", {
						address,
						topics
					}],
					onData(data) {
						if (!active) return;
						const log = data.result;
						try {
							const { eventName, args: args$1 } = decodeEventLog({
								abi: events_ ?? [],
								data: log.data,
								topics: log.topics,
								strict: isStrict
							});
							const formatted = formatLog(log, {
								args: args$1,
								eventName
							});
							onLogs([formatted]);
						} catch (error) {
							let eventName;
							let isUnnamed;
							if (error instanceof DecodeLogDataMismatch || error instanceof DecodeLogTopicsMismatch) {
								if (isStrict) return;
								eventName = error.abiItem.name;
								isUnnamed = error.abiItem.inputs?.some((x) => !("name" in x) || !x.name);
							}
							const formatted = formatLog(log, {
								args: isUnnamed ? [] : {},
								eventName
							});
							onLogs([formatted]);
						}
					},
					onError: (error) => {
						onError?.(error);
					}
				});
				unsubscribe = unsubscribe_;
				if (!active) unsubscribe();
			} catch (error) {
				onError?.(error);
			}
		})();
		return () => unsubscribe();
	};
	return subscribeShredEvents();
}

//#endregion
//#region src/viem/utils/formatters/shred.ts
const receiptStatuses = {
	"0x0": "reverted",
	"0x1": "success"
};
function formatShredStateChange(stateChange) {
	return Object.entries(stateChange).map(([address, stateChange$1]) => {
		return {
			address,
			balance: BigInt(stateChange$1.balance),
			newCode: stateChange$1.new_code,
			nonce: stateChange$1.nonce,
			storageChanges: Object.entries(stateChange$1.storage).map(([slot, value]) => {
				return {
					slot,
					value
				};
			})
		};
	});
}
function formatShred(shred) {
	return {
		blockNumber: BigInt(shred.block_number),
		shredIndex: shred.shred_idx,
		stateChanges: formatShredStateChange(shred.state_changes),
		transactions: shred.transactions.map(({ receipt, transaction }) => {
			if ("Legacy" in receipt && receipt.Legacy) {
				const tx = transaction;
				return {
					...tx,
					chainId: hexToNumber(tx.chainId),
					cumulativeGasUsed: BigInt(receipt.Legacy.cumulativeGasUsed),
					gas: BigInt(tx.gas),
					gasPrice: BigInt(tx.gasPrice),
					status: receiptStatuses[receipt.Legacy.status],
					nonce: hexToNumber(tx.nonce),
					type: "legacy",
					typeHex: tx.type,
					value: BigInt(tx.value),
					logs: receipt.Legacy.logs,
					v: BigInt(tx.v)
				};
			} else if ("Eip1559" in receipt) {
				const tx = transaction;
				return {
					...tx,
					chainId: hexToNumber(tx.chainId),
					cumulativeGasUsed: BigInt(receipt.Eip1559.cumulativeGasUsed),
					gas: BigInt(tx.gas),
					maxFeePerGas: BigInt(tx.maxFeePerGas),
					maxPriorityFeePerGas: BigInt(tx.maxPriorityFeePerGas),
					status: receiptStatuses[receipt.Eip1559.status],
					nonce: hexToNumber(tx.nonce),
					type: "eip1559",
					typeHex: tx.type,
					value: BigInt(tx.value),
					logs: receipt.Eip1559.logs,
					v: BigInt(tx.v)
				};
			} else throw new Error("Unknown tx type");
		})
	};
}

//#endregion
//#region src/viem/actions/shred/watchShreds.ts
/**
* Watches for new shreds on the RISE network.
*
* @param client - Client to use.
* @param parameters - {@link WatchShredsParameters}
* @returns A function that can be used to unsubscribe from the shred.
*/
function watchShreds(client, { onShred, onError }) {
	const transport_ = (() => {
		if (client.transport.type === "webSocket") return client.transport;
		const wsTransport = client.transport?.transports.find((transport) => transport.config.type === "webSocket");
		if (!wsTransport) throw new Error("A shredWebSocket transport is required");
		return wsTransport.value;
	})();
	const subscribeShreds = () => {
		let active = true;
		let unsubscribe = () => {
			active = false;
		};
		(async () => {
			try {
				const { unsubscribe: unsubscribe_ } = await transport_.riseSubscribe({
					params: [],
					onData: (data) => {
						if (!active) return;
						const shred = data.result;
						onShred(formatShred(shred));
					},
					onError: (error) => {
						onError?.(error);
					}
				});
				unsubscribe = unsubscribe_;
				if (!active) unsubscribe();
			} catch (error) {
				onError?.(error);
			}
		})();
		return () => unsubscribe();
	};
	return subscribeShreds();
}

//#endregion
//#region src/viem/clients/decorators/shred.ts
function shredActions(client) {
	return {
		watchContractShredEvent: (args) => watchContractShredEvent(client, args),
		watchShredEvent: (args) => watchShredEvent(client, args),
		watchShreds: (args) => watchShreds(client, args)
	};
}

//#endregion
//#region src/viem/clients/createPublicShredClient.ts
function createPublicShredClient(parameters) {
	return createPublicClient({ ...parameters }).extend(shredActions);
}

//#endregion
//#region src/viem/actions/shred/sendRawTransactionSync.ts
/**
* Sends a raw transaction to the RISE network, where it is processed as a shred,
* and waits for its real-time confirmation.
*
* @param client - Client to use.
* @param parameters - {@link SendRawTransactionSyncParameters}
* @returns The transaction hash. {@link SendRawTransactionSyncReturnType}
*/
function sendRawTransactionSync(client, { serializedTransaction }) {
	return client.request({
		method: "eth_sendRawTransactionSync",
		params: [serializedTransaction]
	}, { retryCount: 0 });
}

//#endregion
//#region src/viem/clients/decorators/sync.ts
function syncActions(client) {
	return { sendRawTransactionSync: (args) => sendRawTransactionSync(client, args) };
}

//#endregion
//#region src/viem/clients/createPublicSyncClient.ts
function createPublicSyncClient(parameters) {
	return createPublicClient({ ...parameters }).extend(syncActions);
}

//#endregion
//#region src/viem/utils/promise/withResolvers.ts
/** @internal */
function withResolvers() {
	let resolve = () => void 0;
	let reject = () => void 0;
	const promise = new Promise((resolve_, reject_) => {
		resolve = resolve_;
		reject = reject_;
	});
	return {
		promise,
		resolve,
		reject
	};
}

//#endregion
//#region src/viem/utils/promise/createBatchScheduler.ts
const schedulerCache = /* @__PURE__ */ new Map();
/** @internal */
function createBatchScheduler({ fn, id, shouldSplitBatch, wait = 0, sort }) {
	const exec = () => {
		const scheduler = getScheduler();
		flush();
		const args = scheduler.map(({ args: args$1 }) => args$1);
		if (args.length === 0) return;
		fn(args).then((data) => {
			if (sort && Array.isArray(data)) data.sort(sort);
			for (const [i, { resolve }] of scheduler.entries()) resolve?.([data[i], data]);
		}).catch((error) => {
			for (const { reject } of scheduler) reject?.(error);
		});
	};
	const flush = () => schedulerCache.delete(id);
	const getBatchedArgs = () => getScheduler().map(({ args }) => args);
	const getScheduler = () => schedulerCache.get(id) || [];
	const setScheduler = (item) => schedulerCache.set(id, [...getScheduler(), item]);
	return {
		flush,
		schedule(args) {
			const { promise, resolve, reject } = withResolvers();
			const split = shouldSplitBatch?.([...getBatchedArgs(), args]);
			if (split) exec();
			const hasActiveScheduler = getScheduler().length > 0;
			if (hasActiveScheduler) {
				setScheduler({
					args,
					resolve,
					reject
				});
				return promise;
			}
			setScheduler({
				args,
				resolve,
				reject
			});
			setTimeout(exec, wait);
			return promise;
		}
	};
}

//#endregion
//#region src/viem/utils/rpc/id.ts
function createIdStore() {
	return {
		current: 0,
		take() {
			return this.current++;
		},
		reset() {
			this.current = 0;
		}
	};
}
const idCache = /* @__PURE__ */ createIdStore();

//#endregion
//#region src/viem/utils/rpc/socket.ts
const socketClientCache = /* @__PURE__ */ new Map();
async function getSocketRpcClient(parameters) {
	const { getSocket, keepAlive = true, key = "socket", reconnect = true, url } = parameters;
	const { interval: keepAliveInterval = 3e4 } = typeof keepAlive === "object" ? keepAlive : {};
	const { attempts = 5, delay = 2e3 } = typeof reconnect === "object" ? reconnect : {};
	let socketClient = socketClientCache.get(`${key}:${url}`);
	if (socketClient) return socketClient;
	let reconnectCount = 0;
	const { schedule } = createBatchScheduler({
		id: `${key}:${url}`,
		fn: async () => {
			const requests = /* @__PURE__ */ new Map();
			const subscriptions = /* @__PURE__ */ new Map();
			let error;
			let socket;
			let keepAliveTimer;
			async function setup() {
				const result = await getSocket({
					onClose() {
						for (const request of requests.values()) request.onError?.(new SocketClosedError({ url }));
						for (const subscription of subscriptions.values()) subscription.onError?.(new SocketClosedError({ url }));
						if (reconnect && reconnectCount < attempts) setTimeout(async () => {
							reconnectCount++;
							await setup().catch(console.error);
						}, delay);
						else {
							requests.clear();
							subscriptions.clear();
						}
					},
					onError(error_) {
						error = error_;
						for (const request of requests.values()) request.onError?.(error);
						for (const subscription of subscriptions.values()) subscription.onError?.(error);
						socketClient?.close();
						if (reconnect && reconnectCount < attempts) setTimeout(async () => {
							reconnectCount++;
							await setup().catch(console.error);
						}, delay);
						else {
							requests.clear();
							subscriptions.clear();
						}
					},
					onOpen() {
						error = void 0;
						reconnectCount = 0;
					},
					onResponse(data) {
						const isSubscription = data.method === "rise_subscription";
						const id = isSubscription ? data.params.subscription : data.id;
						const cache = isSubscription ? subscriptions : requests;
						const callback = cache.get(id);
						if (callback) callback.onResponse(data);
						if (!isSubscription) cache.delete(id);
					}
				});
				socket = result;
				if (keepAlive) {
					if (keepAliveTimer) clearInterval(keepAliveTimer);
					keepAliveTimer = setInterval(() => socket.ping?.(), keepAliveInterval);
				}
				return result;
			}
			await setup();
			error = void 0;
			socketClient = {
				close() {
					keepAliveTimer && clearInterval(keepAliveTimer);
					socket.close();
					socketClientCache.delete(`${key}:${url}`);
				},
				get socket() {
					return socket;
				},
				request({ body, onError, onResponse }) {
					if (error && onError) onError(error);
					const id = body.id ?? idCache.take();
					const callback = (response) => {
						if (typeof response.id === "number" && id !== response.id) return;
						if (body.method === "rise_subscribe" && typeof response.result === "string") subscriptions.set(response.result, {
							onResponse: callback,
							onError
						});
						if (body.method === "rise_unsubscribe") subscriptions.delete(body.params?.[0]);
						onResponse(response);
					};
					requests.set(id, {
						onResponse: callback,
						onError
					});
					try {
						socket.request({ body: {
							jsonrpc: "2.0",
							id,
							...body
						} });
					} catch (error$1) {
						onError?.(error$1);
					}
				},
				requestAsync({ body, timeout = 1e4 }) {
					return withTimeout(() => new Promise((onResponse, onError) => this.request({
						body,
						onError,
						onResponse
					})), {
						errorInstance: new TimeoutError({
							body,
							url
						}),
						timeout
					});
				},
				requests,
				subscriptions,
				url
			};
			socketClientCache.set(`${key}:${url}`, socketClient);
			return [socketClient];
		}
	});
	const [, [socketClient_]] = await schedule();
	return socketClient_;
}

//#endregion
//#region src/viem/utils/rpc/webSocket.ts
function getWebSocketRpcClient(url, options = {}) {
	const { keepAlive, reconnect } = options;
	return getSocketRpcClient({
		async getSocket({ onClose, onError, onOpen, onResponse }) {
			const WebSocket = await import("isows").then((module) => module.WebSocket);
			const socket = new WebSocket(url);
			function onClose_() {
				socket.removeEventListener("close", onClose_);
				socket.removeEventListener("message", onMessage);
				socket.removeEventListener("error", onError);
				socket.removeEventListener("open", onOpen);
				onClose();
			}
			function onMessage({ data }) {
				try {
					const _data = JSON.parse(data);
					onResponse(_data);
				} catch (error) {
					onError(error);
				}
			}
			socket.addEventListener("close", onClose_);
			socket.addEventListener("message", onMessage);
			socket.addEventListener("error", onError);
			socket.addEventListener("open", onOpen);
			if (socket.readyState === WebSocket.CONNECTING) await new Promise((resolve, reject) => {
				if (!socket) return;
				socket.addEventListener("open", resolve);
				socket.addEventListener("error", (error) => {
					reject(new WebSocketRequestError({
						url: socket.url,
						cause: error
					}));
				});
			});
			const { close: close_ } = socket;
			return Object.assign(socket, {
				close() {
					close_.bind(socket)();
					onClose_();
				},
				ping() {
					try {
						if (socket.readyState === socket.CLOSED || socket.readyState === socket.CLOSING) throw new WebSocketRequestError({
							url: socket.url,
							cause: new SocketClosedError({ url: socket.url })
						});
						const body = {
							jsonrpc: "2.0",
							method: "net_version",
							params: []
						};
						socket.send(JSON.stringify(body));
					} catch (error) {
						onError(error);
					}
				},
				request({ body }) {
					if (socket.readyState === socket.CLOSED || socket.readyState === socket.CLOSING) throw new WebSocketRequestError({
						body,
						url: socket.url,
						cause: new SocketClosedError({ url: socket.url })
					});
					return socket.send(JSON.stringify(body));
				}
			});
		},
		keepAlive,
		reconnect,
		url
	});
}

//#endregion
//#region src/viem/clients/transports/shredsWebSocket.ts
function shredsWebSocket(url, config = {}) {
	const { keepAlive, reconnect } = config;
	const ws = webSocket(url, config);
	return (params) => {
		const { chain } = params;
		const ws_ = ws(params);
		const url_ = url || chain?.rpcUrls.default.webSocket?.[0];
		const wsRpcClientOpts = {
			keepAlive,
			reconnect
		};
		if (!url_) throw new UrlRequiredError();
		return {
			config: ws_.config,
			request: ws_.request,
			value: ws_.value ? {
				getSocket: ws_.value.getSocket,
				getRpcClient: ws_.value.getRpcClient,
				subscribe: ws_.value.subscribe,
				async riseSubscribe({ params: params$1, onData, onError }) {
					const rpcClient = await getWebSocketRpcClient(url_, wsRpcClientOpts);
					const { result: subscriptionId } = await new Promise((resolve, reject) => rpcClient.request({
						body: {
							method: "rise_subscribe",
							params: params$1
						},
						onError(error) {
							reject(error);
							onError?.(error);
							return;
						},
						onResponse(response) {
							if (response.error) {
								reject(response.error);
								onError?.(response.error);
								return;
							}
							if (typeof response.id === "number") {
								resolve(response);
								return;
							}
							if (response.method !== "rise_subscription") return;
							onData(response.params);
						}
					}));
					return {
						subscriptionId,
						unsubscribe() {
							return new Promise((resolve) => rpcClient.request({
								body: {
									method: "rise_unsubscribe",
									params: [subscriptionId]
								},
								onResponse: resolve
							}));
						}
					};
				}
			} : void 0
		};
	};
}

//#endregion
export { createPublicShredClient, createPublicSyncClient, formatShred, formatShredStateChange, receiptStatuses, sendRawTransactionSync, shredActions, shredsWebSocket, syncActions, watchContractShredEvent, watchShredEvent, watchShreds };